import { FC, useState, useRef, useCallback } from 'react';
import apiService from '@/services/api.service';

interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  folder: 'thumbnails' | 'badges' | 'avatars';
  label?: string;
  hint?: string;
  maxSizeMB?: number;
  className?: string;
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const ImageUpload: FC<ImageUploadProps> = ({
  value,
  onChange,
  folder,
  label = 'Imagem',
  hint,
  maxSizeMB = 5,
  className = '',
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const upload = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError('Tipo não suportado. Use JPEG, PNG, WebP ou GIF.');
        return;
      }
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo: ${maxSizeMB}MB.`);
        return;
      }

      setError(null);
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);

        const { data } = await apiService.post<{ url: string; filename: string }>(
          '/upload/image/',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        onChange(data.url);
      } catch {
        setError('Erro ao fazer upload. Tente novamente.');
      } finally {
        setIsUploading(false);
      }
    },
    [folder, maxSizeMB, onChange]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) upload(file);
    },
    [upload]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleRemove = () => {
    onChange('');
    setError(null);
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium mb-1">{label}</label>}

      {value ? (
        <div className="relative group">
          <div className="w-full h-48 rounded-xl overflow-hidden border border-theme-border bg-surface-card">
            <img
              src={value}
              alt="Preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '';
                (e.target as HTMLImageElement).alt = 'Erro ao carregar imagem';
              }}
            />
          </div>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/30 transition-colors"
            >
              <span className="material-symbols-outlined text-base align-middle mr-1">swap_horiz</span>
              Trocar
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="px-3 py-1.5 rounded-lg bg-red-500/30 backdrop-blur-sm text-white text-sm font-medium hover:bg-red-500/50 transition-colors"
            >
              <span className="material-symbols-outlined text-base align-middle mr-1">delete</span>
              Remover
            </button>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`w-full h-48 rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${
            isDragOver
              ? 'border-accent bg-accent/5'
              : 'border-theme-border hover:border-accent/40 hover:bg-surface-card/50'
          } ${isUploading ? 'pointer-events-none opacity-60' : ''}`}
        >
          {isUploading ? (
            <>
              <div className="size-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-content-muted">Enviando...</span>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-3xl text-content-muted">cloud_upload</span>
              <span className="text-sm text-content-muted">
                Arraste uma imagem ou <span className="text-accent font-medium">clique para selecionar</span>
              </span>
              <span className="text-xs text-content-muted/60">JPEG, PNG, WebP ou GIF (máx. {maxSizeMB}MB)</span>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        className="hidden"
      />

      {error && <p className="text-red-400 text-sm mt-1">{error}</p>}
      {hint && !error && <p className="text-xs text-content-muted mt-1">{hint}</p>}
    </div>
  );
};

export default ImageUpload;
